/**
 * Data layer for `igaming-brand-overrides`. Thin typed fetch wrappers over /api/v1/igaming-brand-overrides.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingBrandOverridesRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingBrandOverridesResponse {
	count: number;
	items: IgamingBrandOverridesRow[];
}

export interface CreateIgamingBrandOverridesBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-brand-overrides";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingBrandOverridesResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingBrandOverridesResponse>(res);
}

export async function create(body: CreateIgamingBrandOverridesBody): Promise<IgamingBrandOverridesRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingBrandOverridesRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingBrandOverridesQueryKeys = {
	all: ["igaming-brand-overrides"] as const,
	list: () => [...igamingBrandOverridesQueryKeys.all] as const,
	detail: (id: string) => [...igamingBrandOverridesQueryKeys.all, id] as const,
};
